import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, ArrowLeft, LogIn, BadgeCheck, MessageCircle, ChevronLeft, ChevronRight, StickyNote, IndianRupee, Users, TrendingUp, Star, RefreshCw, Sun, Moon, Upload, Download, Plus, X, FileText } from "lucide-react";
import { api, googleLogin, logout } from "@/lib/api";
import { Logo } from "@/components/Logo";

const INTEREST_META = {
  "luxury-apartments": { title: "Luxury Apartments", txn: "Luxury Apartment Advisory Services", otherPath: "/wealth-management", otherLabel: "Wealth Management" },
  "mutual-funds": { title: "Wealth Management", txn: "Wealth Management Advisory Services", otherPath: "/luxury-apartments", otherLabel: "Luxury Apartments" },
};

const STAGES = [
  { key: "new", label: "New Enquiry" },
  { key: "qualified", label: "Qualified" },
  { key: "contacted", label: "Site Visit / In Talks" },
  { key: "closed", label: "Closed" },
];

const TICKET_VALUES = { "under-50L": 0.35, "50L-2Cr": 1.25, "2Cr+": 2.5 };
const TICKET_LABELS = { "under-50L": "Under ₹50L", "50L-2Cr": "₹50L–2Cr", "2Cr+": "₹2Cr+" };

const inr = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e7) return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n / 1e7)} Cr`;
  if (n >= 1e5) return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 }).format(n / 1e5)} L`;
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;
};

const waLink = (l, metaTitle) =>
  `https://wa.me/${(l.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(
    `Hello ${l.name}, this is Keyscape Advisory regarding your ${metaTitle.toLowerCase()} enquiry. When would be a good time to speak?`
  )}`;

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-white/10 bg-white/[0.02] p-6" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">{label}</p>
      <Icon size={16} className="text-[#C8A97E]" />
    </div>
    <p className="font-display text-3xl md:text-4xl text-[#F8F9FA] mt-4">{value}</p>
  </div>
);

const DealCard = ({ lead, onMove, onInvoice, metaTitle }) => {
  const stageIdx = STAGES.findIndex((s) => s.key === lead.status);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="border border-white/10 bg-white/[0.03] p-5 space-y-3"
      data-testid={`luxury-deal-${lead.lead_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-[#F8F9FA] flex items-center gap-2">
          {lead.name}
          {lead.verified_google && <BadgeCheck size={14} className="text-[#C8A97E]" />}
        </p>
        <span className="font-mono-lux text-[10px] text-[#C8A97E] border border-[#C8A97E]/40 px-2 py-0.5 flex items-center gap-1">
          <Star size={10} /> {lead.score}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {lead.budget && (
          <span className="font-mono-lux text-[9px] uppercase tracking-[0.15em] text-[#F8F9FA] bg-[#C8A97E]/10 border border-[#C8A97E]/30 px-2 py-1">
            Ticket {TICKET_LABELS[lead.budget] || lead.budget}
          </span>
        )}
        {(lead.notes || []).length > 0 && (
          <span className="font-mono-lux text-[9px] uppercase tracking-[0.15em] text-[#8A8D98] border border-white/10 px-2 py-1 flex items-center gap-1">
            <StickyNote size={10} /> {lead.notes.length}
          </span>
        )}
        {lead.invoice_no && (
          <span className="font-mono-lux text-[9px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-900 px-2 py-1" data-testid={`luxury-invno-${lead.lead_id}`}>
            {lead.invoice_no}
          </span>
        )}
      </div>

      {lead.message && <p className="text-xs text-[#8A8D98] leading-relaxed line-clamp-2">{lead.message}</p>}

      <p className="font-mono-lux text-[10px] text-[#8A8D98] flex items-center gap-2">
        {lead.email}
        {lead.phone && (
          <a href={waLink(lead, metaTitle)} target="_blank" rel="noopener noreferrer" data-testid={`luxury-whatsapp-${lead.lead_id}`} title="WhatsApp follow-up" className="text-emerald-400 hover:text-emerald-300 transition-colors">
            <MessageCircle size={13} />
          </a>
        )}
      </p>

      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <button
          onClick={() => onMove(lead, STAGES[stageIdx - 1].key)}
          disabled={stageIdx === 0}
          data-testid={`luxury-move-back-${lead.lead_id}`}
          className="text-[#8A8D98] hover:text-[#C8A97E] disabled:opacity-20 transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="font-mono-lux text-[9px] uppercase tracking-[0.2em] text-[#8A8D98]">{STAGES[stageIdx]?.label}</span>
        <button onClick={() => onInvoice(lead)} data-testid={`luxury-invoice-${lead.lead_id}`} title="Generate Invoice" className="text-[#8A8D98] hover:text-[#C8A97E] transition-colors">
          <FileText size={15} />
        </button>
        <button
          onClick={() => onMove(lead, STAGES[stageIdx + 1].key)}
          disabled={stageIdx === STAGES.length - 1}
          data-testid={`luxury-move-forward-${lead.lead_id}`}
          className="text-[#8A8D98] hover:text-[#C8A97E] disabled:opacity-20 transition-colors"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </motion.div>
  );
};

export default function LuxuryPipeline({ interest = "luxury-apartments" }) {
  const meta = INTEREST_META[interest] || INTEREST_META["luxury-apartments"];
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [state, setState] = useState(location.state?.user ? "ready" : "checking");
  const [leads, setLeads] = useState([]);
  const [theme, setTheme] = useState("dark");
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "", budget: "" });
  const [dupDialog, setDupDialog] = useState(null);
  const [invoiceLead, setInvoiceLead] = useState(null);
  const [invOpts, setInvOpts] = useState({ fee: "", gstin: "", place_of_supply: "Maharashtra", transaction_type: meta.txn, apply_tds: true, tds_rate: 2 });

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (!res.data.is_admin) {
          setState("denied");
        } else {
          setUser(res.data);
          setState("ready");
        }
      } catch {
        setState("denied");
      }
    })();
  }, [location.state]);

  const loadLeads = () => {
    api.get("/admin/leads")
      .then((r) => setLeads(r.data.filter((l) => l.interest === interest)))
      .catch(() => toast.error("Could not load pipeline data"));
    setRefreshedAt(new Date());
  };

  useEffect(() => {
    if (state === "ready" && user?.is_admin) loadLeads();
  }, [state, user, interest]);

  const moveDeal = async (lead, status) => {
    try {
      const res = await api.patch(`/admin/leads/${lead.lead_id}`, { status });
      setLeads((ls) => ls.map((l) => (l.lead_id === lead.lead_id ? res.data : l)));
      toast.success(`${lead.name} moved to ${STAGES.find((s) => s.key === status)?.label}.`);
    } catch {
      toast.error("Could not move deal.");
    }
  };

  const exportExcel = () => {
    const headers = ["Name", "Email", "Phone", "Budget", "Status", "Score", "Invoice No", "Message", "Created"];
    const rows = leads.map((l) => [l.name, l.email, l.phone || "", l.budget || "", l.status, l.score, l.invoice_no || "", (l.message || "").replace(/\n/g, " "), l.created_at]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${interest}-pipeline.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported to Excel (CSV)");
  };

  const uploadExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const head = (lines[0] || "").split(",").map((h) => h.replace(/"/g, "").trim().toLowerCase());
      const ni = head.indexOf("name");
      const ei = head.indexOf("email");
      const pi = head.indexOf("phone");
      const bi = head.indexOf("budget");
      if (ni === -1 || ei === -1) throw new Error("File needs Name and Email columns");
      const parsed = [];
      for (const line of lines.slice(1)) {
        const cols = line.match(/("([^"]|"")*"|[^,]*)(,|$)/g)?.map((c) => c.replace(/,$/, "").replace(/^"|"$/g, "").replace(/""/g, '"')) || [];
        const name = cols[ni];
        const email = cols[ei];
        if (!name || !email) continue;
        parsed.push({ name, email, phone: pi > -1 ? cols[pi] || "" : "", budget: bi > -1 ? cols[bi] || "" : "" });
      }
      const byEmail = new Map(leads.map((l) => [(l.email || "").toLowerCase(), l]));
      const fresh = [];
      const dupes = [];
      for (const r of parsed) {
        const existing = byEmail.get(r.email.toLowerCase());
        if (existing) dupes.push({ row: r, existing });
        else fresh.push(r);
      }
      if (!dupes.length) {
        for (const r of fresh) {
          await api.post("/leads", { name: r.name, email: r.email, phone: r.phone || null, interest, budget: r.budget || null, message: "Imported via Excel upload" });
        }
        toast.success(`Imported ${fresh.length} records from Excel.`);
        loadLeads();
      } else {
        setDupDialog({ fresh, dupes });
        toast.info(`${dupes.length} duplicate record(s) found — choose how to proceed.`);
      }
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const resolveDupes = async (mode) => {
    if (!dupDialog) return;
    setUploading(true);
    try {
      let imported = 0, overwritten = 0, created = 0;
      for (const r of dupDialog.fresh) {
        await api.post("/leads", { name: r.name, email: r.email, phone: r.phone || null, interest, budget: r.budget || null, message: "Imported via Excel upload" });
        imported++;
      }
      for (const { row, existing } of dupDialog.dupes) {
        if (mode === "create") {
          await api.post("/leads", { name: row.name, email: row.email, phone: row.phone || null, interest, budget: row.budget || null, message: "Imported via Excel upload (duplicate)" });
          created++;
        } else if (mode === "overwrite") {
          await api.patch(`/admin/leads/${existing.lead_id}`, { phone: row.phone || null, budget: row.budget || null });
          overwritten++;
        }
      }
      const parts = [`${imported} new`];
      if (overwritten) parts.push(`${overwritten} overwritten`);
      if (created) parts.push(`${created} created as new`);
      if (mode === "skip") parts.push(`${dupDialog.dupes.length} skipped`);
      toast.success(`Upload complete — ${parts.join(", ")}.`);
      setDupDialog(null);
      loadLeads();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/leads", {
        name: addForm.name, email: addForm.email,
        phone: addForm.phone || null, interest,
        budget: addForm.budget || null, message: null,
      });
      toast.success(`${addForm.name} added to the board.`);
      setShowAdd(false);
      setAddForm({ name: "", email: "", phone: "", budget: "" });
      loadLeads();
    } catch { toast.error("Could not add deal."); }
    finally { setAdding(false); }
  };

  const openInvoice = (lead) => {
    setInvoiceLead(lead);
    setInvOpts((o) => ({ ...o, fee: "", transaction_type: meta.txn }));
  };

  const downloadLeadPdf = async () => {
    if (!invoiceLead) return;
    if (!Number(invOpts.fee)) {
      toast.error("Enter the advisory fee amount.");
      return;
    }
    try {
      const params = new URLSearchParams({
        fee: invOpts.fee, gstin: invOpts.gstin, place_of_supply: invOpts.place_of_supply,
        transaction_type: invOpts.transaction_type, apply_tds: invOpts.apply_tds, tds_rate: invOpts.tds_rate,
      });
      const res = await api.get(`/admin/leads/${invoiceLead.lead_id}/invoice.pdf?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoiceLead.lead_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
      loadLeads();
    } catch { toast.error("Download failed"); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center" data-testid="luxury-loading">
        <p className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#8A8D98] animate-pulse">Verifying access…</p>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex flex-col items-center justify-center gap-8" data-testid="luxury-access-denied">
        <p className="font-display text-3xl text-[#F8F9FA]">This desk is private.</p>
        <p className="text-sm text-[#8A8D98]">Sign in with an authorised Google workspace ID to view the {meta.title.toLowerCase()} pipeline.</p>
        <button onClick={() => googleLogin(`/${interest === "mutual-funds" ? "wealth-management" : "luxury-apartments"}`)} data-testid="luxury-login-button" className="btn-gold flex items-center gap-3 px-8 py-4 text-[11px] font-mono-lux uppercase tracking-[0.2em] font-medium">
          <span>Continue with Google</span>
          <LogIn size={15} className="relative z-10" />
        </button>
        <Link to="/" className="font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] hover:text-[#C8A97E] transition-colors" data-testid="luxury-back-link">
          Back to site
        </Link>
      </div>
    );
  }

  const openDeals = leads.filter((l) => l.status !== "closed");
  const pipelineValue = openDeals.reduce((sum, l) => sum + (TICKET_VALUES[l.budget] || 0), 0);
  const qualified = leads.filter((l) => l.status !== "new").length;
  const avgScore = leads.length ? Math.round(leads.reduce((s, l) => s + (l.score || 0), 0) / leads.length) : 0;
  const invFee = Number(invOpts.fee) || 0;
  const invGst = invFee * 0.18;
  const invTds = invOpts.apply_tds ? invFee * (Number(invOpts.tds_rate) || 0) / 100 : 0;

  return (
    <div className={`min-h-screen bg-[#0B0C10] px-6 md:px-10 py-8 ${theme === "light" ? "light-theme" : ""}`} data-testid="luxury-pipeline">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-center justify-between pb-6 border-b border-white/10 gap-4 flex-wrap">
          <div className="flex items-center gap-6">
            <Logo testId="luxury-logo" />
            <Link to="/land-acquisition" data-testid="luxury-land-link" className="hidden lg:flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] hover:text-[#C8A97E] transition-colors">
              <ArrowLeft size={14} /> Land Deals
            </Link>
            <Link to={meta.otherPath} data-testid="luxury-other-link" className="hidden lg:flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] hover:text-[#C8A97E] transition-colors">
              {meta.otherLabel} →
            </Link>
            <div>
              <p className="font-mono-lux text-[9px] uppercase tracking-[0.3em] text-[#C8A97E]">Deal Pipeline</p>
              <h1 className="font-display text-2xl md:text-3xl text-[#F8F9FA]" data-testid="luxury-title">{meta.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2.5 flex-wrap justify-end">
            <span className="hidden md:block font-mono-lux text-[9px] uppercase tracking-[0.2em] text-[#8A8D98]" data-testid="luxury-as-on">
              as on {refreshedAt ? refreshedAt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
            </span>
            <button onClick={loadLeads} data-testid="luxury-refresh" title="Refresh" className="border border-white/15 hover:border-[#C8A97E] text-[#8A8D98] p-2.5 transition-colors">
              <RefreshCw size={13} />
            </button>
            <button onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} data-testid="luxury-theme-toggle" className="border border-white/15 hover:border-[#C8A97E] text-[#8A8D98] px-4 py-2.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors">
              {theme === "dark" ? <Sun size={13} /> : <Moon size={13} />} {theme === "dark" ? "Light" : "Dark"}
            </button>
            <label data-testid="luxury-upload" className={`border border-white/15 hover:border-[#C8A97E] text-[#8A8D98] px-4 py-2.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors cursor-pointer ${uploading ? "opacity-50" : ""}`}>
              <Upload size={13} /> {uploading ? "Uploading…" : "Upload in Excel"}
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={uploadExcel} disabled={uploading} data-testid="luxury-upload-input" />
            </label>
            <button onClick={exportExcel} data-testid="luxury-export" className="border border-white/15 hover:border-[#C8A97E] text-[#8A8D98] px-4 py-2.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors">
              <Download size={13} /> Export in Excel
            </button>
            <button onClick={() => setShowAdd(true)} data-testid="luxury-add-deal" className="bg-[#C8A97E] hover:bg-[#b8986a] text-[#0B0C10] px-4 py-2.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2 transition-colors">
              <Plus size={13} /> Add New Deal
            </button>
            <button onClick={handleLogout} data-testid="luxury-logout-button" className="text-[#8A8D98] hover:text-red-400 transition-colors ml-1" title="Sign out"><LogOut size={15} /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 py-8">
          <StatCard icon={Users} label="Total Deals" value={leads.length} testId="luxury-stat-total" />
          <StatCard icon={TrendingUp} label="Qualified+" value={qualified} testId="luxury-stat-qualified" />
          <StatCard icon={IndianRupee} label="Pipeline Value (indicative)" value={pipelineValue ? `₹${pipelineValue.toFixed(2)} Cr` : "—"} testId="luxury-stat-value" />
          <StatCard icon={Star} label="Avg Deal Score" value={leads.length ? avgScore : "—"} testId="luxury-stat-score" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 pb-12" data-testid="luxury-board">
          {STAGES.map((stage) => {
            const stageLeads = leads.filter((l) => l.status === stage.key);
            return (
              <div key={stage.key} className="border border-white/10 bg-white/[0.01] p-4 min-h-[300px]" data-testid={`luxury-stage-${stage.key}`}>
                <div className="flex items-center justify-between px-1 pb-4 border-b border-white/10 mb-4">
                  <p className="font-mono-lux text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">{stage.label}</p>
                  <span className="font-display text-xl text-[#C8A97E]" data-testid={`luxury-stage-count-${stage.key}`}>{stageLeads.length}</span>
                </div>
                <div className="space-y-4">
                  {stageLeads.length === 0 && (
                    <p className="text-xs text-[#8A8D98]/60 px-1 py-6 text-center">No deals here yet.</p>
                  )}
                  {stageLeads.map((l) => (
                    <DealCard key={l.lead_id} lead={l} onMove={moveDeal} onInvoice={openInvoice} metaTitle={meta.title} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {showAdd && (
          <div className="fixed inset-0 z-50" data-testid="lux-add-overlay">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowAdd(false)} />
            <aside className="absolute inset-y-0 right-0 w-full max-w-md bg-[#0F1116] border-l border-white/10 p-8 overflow-y-auto" data-testid="lux-add-drawer">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <p className="font-mono-lux text-[10px] uppercase tracking-[0.3em] text-[#C8A97E]">{meta.title}</p>
                  <h2 className="font-display text-2xl text-[#F8F9FA] mt-2">Add New Deal</h2>
                </div>
                <button onClick={() => setShowAdd(false)} data-testid="lux-add-close" className="text-[#8A8D98] hover:text-[#F8F9FA] transition-colors"><X size={18} /></button>
              </div>
              <form onSubmit={submitAdd} className="space-y-7" data-testid="lux-add-form">
                <input required placeholder="Client name" value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} className="lux-input" data-testid="lux-add-name" />
                <input required type="email" placeholder="Email address" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} className="lux-input" data-testid="lux-add-email" />
                <input placeholder="Phone (enables WhatsApp follow-up)" value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} className="lux-input" data-testid="lux-add-phone" />
                <select value={addForm.budget} onChange={(e) => setAddForm((f) => ({ ...f, budget: e.target.value }))} className="lux-input" data-testid="lux-add-budget">
                  <option value="">Ticket size (optional)</option>
                  <option value="under-50L">Under ₹50 Lakhs</option>
                  <option value="50L-2Cr">₹50 Lakhs — ₹2 Crore</option>
                  <option value="2Cr+">₹2 Crore+</option>
                </select>
                <button type="submit" disabled={adding} data-testid="lux-add-submit" className="btn-gold flex items-center gap-3 px-8 py-4 text-[11px] font-mono-lux uppercase tracking-[0.2em] font-medium disabled:opacity-50">
                  <span>{adding ? "Adding…" : "Add to Board"}</span>
                  <Plus size={15} className="relative z-10" />
                </button>
              </form>
            </aside>
          </div>
        )}

        {invoiceLead && (
          <div className="fixed inset-0 z-50" data-testid="lux-invoice-overlay">
            <div className="absolute inset-0 bg-black/60" onClick={() => setInvoiceLead(null)} />
            <aside className="absolute inset-y-0 right-0 w-full max-w-md bg-[#0F1116] border-l border-white/10 p-8 overflow-y-auto" data-testid="lux-invoice-drawer">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <p className="font-mono-lux text-[10px] uppercase tracking-[0.3em] text-[#C8A97E]">Invoice Preview</p>
                  <h2 className="font-display text-2xl text-[#F8F9FA] mt-2">{invoiceLead.name}</h2>
                  <p className="font-mono-lux text-[10px] text-[#8A8D98] mt-1">{invoiceLead.invoice_no || "Invoice no assigned on first download"}</p>
                </div>
                <button onClick={() => setInvoiceLead(null)} data-testid="lux-invoice-close" className="text-[#8A8D98] hover:text-[#F8F9FA] transition-colors"><X size={18} /></button>
              </div>
              <div className="space-y-6">
                <div>
                  <span className="block font-mono-lux text-[9px] uppercase tracking-[0.2em] text-[#8A8D98] mb-1.5">Advisory Fee (₹) *</span>
                  <input type="number" min="0" value={invOpts.fee} onChange={(e) => setInvOpts((o) => ({ ...o, fee: e.target.value }))} className="lux-input" data-testid="lux-invoice-fee" placeholder="e.g. 250000" />
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <span className="block font-mono-lux text-[9px] uppercase tracking-[0.2em] text-[#8A8D98] mb-1.5">Your GSTIN (optional)</span>
                    <input value={invOpts.gstin} onChange={(e) => setInvOpts((o) => ({ ...o, gstin: e.target.value }))} className="lux-input" data-testid="lux-invoice-gstin" />
                  </div>
                  <div>
                    <span className="block font-mono-lux text-[9px] uppercase tracking-[0.2em] text-[#8A8D98] mb-1.5">Place of Supply</span>
                    <input value={invOpts.place_of_supply} onChange={(e) => setInvOpts((o) => ({ ...o, place_of_supply: e.target.value }))} className="lux-input" data-testid="lux-invoice-pos" />
                  </div>
                </div>
                <div>
                  <span className="block font-mono-lux text-[9px] uppercase tracking-[0.2em] text-[#8A8D98] mb-1.5">Transaction Type</span>
                  <input value={invOpts.transaction_type} onChange={(e) => setInvOpts((o) => ({ ...o, transaction_type: e.target.value }))} className="lux-input" data-testid="lux-invoice-txn" />
                </div>
                <label className="flex items-center gap-3 text-sm text-[#B9BCC4] cursor-pointer" data-testid="lux-invoice-tds-toggle">
                  <input type="checkbox" checked={invOpts.apply_tds} onChange={(e) => setInvOpts((o) => ({ ...o, apply_tds: e.target.checked }))} className="accent-[#C8A97E] w-4 h-4" />
                  <span>Apply TDS (Section 194H)
                    <span className="block font-mono-lux text-[9px] text-[#8A8D98] mt-1 uppercase tracking-[0.1em]">Applied on Advisory Fee (excluding GST)</span>
                  </span>
                </label>
                {invOpts.apply_tds && (
                  <div>
                    <span className="block font-mono-lux text-[9px] uppercase tracking-[0.2em] text-[#8A8D98] mb-1.5">TDS Rate %</span>
                    <input type="number" min="0" step="0.1" value={invOpts.tds_rate} onChange={(e) => setInvOpts((o) => ({ ...o, tds_rate: e.target.value }))} className="lux-input" data-testid="lux-invoice-tds-rate" />
                  </div>
                )}

                <div className="border border-white/10 p-5 space-y-2 font-mono-lux text-sm" data-testid="lux-invoice-preview">
                  <p className="flex justify-between text-[#B9BCC4]"><span>Advisory Fee</span><span>{inr(invFee)}</span></p>
                  <p className="flex justify-between text-[#B9BCC4]"><span>GST @ 18%</span><span>{inr(invGst)}</span></p>
                  <p className="flex justify-between text-[#F8F9FA] border-t border-white/10 pt-2"><span>Total Invoice Value</span><span className="text-[#C8A97E]">{inr(invFee + invGst)}</span></p>
                  {invOpts.apply_tds && (
                    <>
                      <p className="flex justify-between text-[#8A8D98] text-xs"><span>Less: TDS u/s 194H @ {invOpts.tds_rate}%</span><span>− {inr(invTds)}</span></p>
                      <p className="flex justify-between text-[#B9BCC4]"><span>Net Payable to Advisor</span><span>{inr(invFee + invGst - invTds)}</span></p>
                    </>
                  )}
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setInvoiceLead(null)} data-testid="lux-invoice-close-btn" className="btn-ghost-lux px-6 py-3.5 text-[10px] font-mono-lux uppercase tracking-[0.2em]">Close</button>
                  <button onClick={downloadLeadPdf} data-testid="lux-invoice-download" className="btn-gold flex items-center gap-2 px-6 py-3.5 text-[10px] font-mono-lux uppercase tracking-[0.2em] font-medium">
                    <span>Download PDF</span>
                    <Download size={14} className="relative z-10" />
                  </button>
                </div>
              </div>
            </aside>
          </div>
        )}

        {dupDialog && (
          <div className="fixed inset-0 z-50" data-testid="lux-dup-dialog-overlay">
            <div className="absolute inset-0 bg-black/60" onClick={() => setDupDialog(null)} />
            <div className="absolute inset-x-4 top-[8%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[560px] bg-[#0F1116] border border-white/10 p-8 max-h-[84vh] overflow-y-auto" data-testid="lux-dup-dialog">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="font-mono-lux text-[10px] uppercase tracking-[0.3em] text-[#C8A97E]">Duplicate Records Found</p>
                  <h2 className="font-display text-2xl text-[#F8F9FA] mt-2">{dupDialog.dupes.length} record{dupDialog.dupes.length === 1 ? "" : "s"} already on the board</h2>
                </div>
                <button onClick={() => setDupDialog(null)} data-testid="lux-dup-close" className="text-[#8A8D98] hover:text-[#F8F9FA] transition-colors"><X size={18} /></button>
              </div>
              <ul className="space-y-2 mb-8">
                {dupDialog.dupes.map(({ row, existing }, i) => (
                  <li key={i} className="border border-white/10 px-4 py-3" data-testid={`lux-dup-row-${i}`}>
                    <p className="text-sm text-[#F8F9FA]">{row.name}</p>
                    <p className="font-mono-lux text-[10px] text-[#8A8D98] mt-0.5">{row.email} · matches existing: {existing.name}</p>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[#8A8D98] mb-6">{dupDialog.fresh.length} new record(s) will be imported regardless. For the duplicates above, choose:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button onClick={() => resolveDupes("overwrite")} data-testid="lux-dup-overwrite" className="bg-[#C8A97E] hover:bg-[#b8986a] text-[#0B0C10] px-4 py-3.5 font-mono-lux text-[10px] uppercase tracking-[0.15em] font-semibold transition-colors">Overwrite Existing</button>
                <button onClick={() => resolveDupes("create")} data-testid="lux-dup-create" className="border border-white/15 hover:border-[#C8A97E] text-[#F8F9FA] px-4 py-3.5 font-mono-lux text-[10px] uppercase tracking-[0.15em] transition-colors">Create New Record</button>
                <button onClick={() => resolveDupes("skip")} data-testid="lux-dup-skip" className="border border-white/15 hover:border-white/40 text-[#8A8D98] px-4 py-3.5 font-mono-lux text-[10px] uppercase tracking-[0.15em] transition-colors">Skip Duplicates</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
